import {
  GoogleAuthProvider,
  getRedirectResult,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  signInWithPopup,
  signInWithRedirect,
  type Auth,
  type User
} from 'firebase/auth';

import { isNativeApp } from '../utils/isNativeApp';

const PENDING_KEY = 'sp_pending_google_credential_v1';

type PendingGoogleCredential = {
  email?: string;
  idToken?: string;
  accessToken?: string;
  createdAt: number;
};

export function getGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export function savePendingGoogleCredential(data: PendingGoogleCredential) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

export function loadPendingGoogleCredential(): PendingGoogleCredential | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.createdAt !== 'number') return null;
    return parsed as PendingGoogleCredential;
  } catch {
    return null;
  }
}

export function clearPendingGoogleCredential() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

export function isAccountExistsDifferentCredentialError(err: any): boolean {
  return err?.code === 'auth/account-exists-with-different-credential';
}

export function extractPendingFromError(err: any): PendingGoogleCredential {
  const email = err?.customData?.email as string | undefined;
  const credential = GoogleAuthProvider.credentialFromError(err) as any;

  return {
    email,
    idToken: credential?.idToken,
    accessToken: credential?.accessToken,
    createdAt: Date.now()
  };
}

export async function signInWithGoogle(auth: Auth): Promise<void> {
  const provider = getGoogleProvider();

  // In app/native prefer redirect.
  if (isNativeApp()) {
    await signInWithRedirect(auth, provider);
    return;
  }

  try {
    await signInWithPopup(auth, provider);
  } catch (err: any) {
    // Popup blocked/closed -> fallback redirect.
    const code = err?.code;
    const fallbackCodes = new Set([
      'auth/popup-blocked',
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request',
      'auth/operation-not-supported-in-this-environment'
    ]);
    if (fallbackCodes.has(code)) {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw err;
  }
}

export async function handleGoogleRedirectResult(auth: Auth): Promise<User | null> {
  // Necessario per finalizzare signInWithRedirect/linkWithRedirect e ottenere errori.
  try {
    const result = await getRedirectResult(auth);
    return result?.user ?? null;
  } catch (err: any) {
    // Propaga solo errori utili al chiamante
    throw err;
  }
}

export async function linkGoogleToCurrentUser(auth: Auth): Promise<void> {
  const provider = getGoogleProvider();

  if (isNativeApp()) {
    await linkWithRedirect(auth.currentUser as User, provider);
    return;
  }

  try {
    await linkWithPopup(auth.currentUser as User, provider);
  } catch (err: any) {
    const code = err?.code;
    const fallbackCodes = new Set([
      'auth/popup-blocked',
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request',
      'auth/operation-not-supported-in-this-environment'
    ]);
    if (fallbackCodes.has(code)) {
      await linkWithRedirect(auth.currentUser as User, provider);
      return;
    }
    throw err;
  }
}

export async function linkPendingCredentialToUser(user: User): Promise<void> {
  const pending = loadPendingGoogleCredential();
  if (!pending?.idToken && !pending?.accessToken) {
    return;
  }

  const credential = GoogleAuthProvider.credential(pending.idToken, pending.accessToken);
  await linkWithCredential(user, credential);
  clearPendingGoogleCredential();
}
